import { Component, OnInit } from '@angular/core';
import { HttpService } from '../service/http.service';
import { ActivatedRoute } from '@angular/router';
import { Vademecum } from '../vademecum';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { medicineAllergyValidator } from '../validator/medicine-allergy-validator';
import { substanceAllergyValidator } from '../validator/substance-allergy-validator';
import { riskSportValidator } from '../validator/risk-sport-validator';
import { frenchSelectValidator } from '../validator/french-select-validator';
import { examDateValidator } from '../validator/exam-date-validator';
import { friendNameValidator } from '../validator/friend-name-validator';
import * as moment from 'moment';
import { FREQUENCIES } from '../frequency-list';
import { LANGUAGES } from '../target-languages-list';
import { BLOODTYPES } from '../blood-type-list';
import { Frequency } from '../frequency';
import { TargetLanguage } from '../target-language';
import { BloodType } from '../blood-type';
import { MedicalInfo } from '../medical-info';
import { FaviconService } from '../favicon.service';
import { PersonalInfo } from '../personal-info';
import { PersonalInfoService } from '../personal-info.service';
import { MessageService } from 'primeng/api';
import { mentalDisorderValidator } from '../validator/mental-disorder-validator';
import { dutchSelectValidator } from '../validator/dutch-select-validator';
import { englishSelectValidator } from '../validator/english-select-validator';

@Component({
  selector: 'app-edit-vademecum',
  templateUrl: './edit-vademecum.component.html',
  styleUrls: ['./edit-vademecum.component.css']
})
export class EditVademecumComponent implements OnInit {

  vademecum!: Vademecum;
  medicalForm!: FormGroup;
  schoolForm!: FormGroup;
  years: number[] = [];
  frequencies: Frequency[] = FREQUENCIES;
  targetLanguages: TargetLanguage[] = LANGUAGES;
  bloodTypes: BloodType[] = BLOODTYPES;
  personalForm!: FormGroup;

  constructor(
    private httpService: HttpService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private favicon: FaviconService,
    private personalInfoService: PersonalInfoService,
    private messageService: MessageService
  ) {
    this.favicon.sbms();
  }

  ngOnInit() {
    this.makeYears();
    let bookingId: string | null = this.route.snapshot.paramMap.get("bookingId")!;
    this.httpService.getVademecumByBookingId(Number(bookingId))
      .subscribe(vademecum => {
        this.vademecum = vademecum;

        let lastSkinTestDate = null;
        if (this.vademecum.lastSkinTestDate != null) {
          lastSkinTestDate = moment(this.vademecum.lastSkinTestDate).format("DD/MM/YYYY");
        }

        if (this.vademecum.experiencedDiseases == null) {
          this.vademecum.experiencedDiseases = "";
        }
        if (this.vademecum.otherDiseases == null) {
          this.vademecum.otherDiseases = "";
        }
        if (this.vademecum.allergiesCauseMedicine == null) {
          this.vademecum.allergiesCauseMedicine = "";
        }
        if (this.vademecum.allergiesCauseSubstance == null) {
          this.vademecum.allergiesCauseSubstance = "";
        }

        if (this.vademecum.medicinesQuantity == null) {
          this.vademecum.medicinesQuantity = "";
        }
        if (this.vademecum.otherInformations == null) {
          this.vademecum.otherInformations = "";
        }
        if (this.vademecum.mentalDisorderDescription == null) {
          this.vademecum.mentalDisorderDescription = "";
        }
        if (this.vademecum.riskDescriptionToSport == null) {
          this.vademecum.riskDescriptionToSport = "";
        }
        if (this.vademecum.matterToReview == null) {
          this.vademecum.matterToReview = "";
        }
        if (this.vademecum.studyBookName == null) {
          this.vademecum.studyBookName = "";
        }
        if (this.vademecum.dutchTeacherName == null) {
          this.vademecum.dutchTeacherName = "";
        }
        if (this.vademecum.englishTeacherName == null) {
          this.vademecum.englishTeacherName = "";
        }
        if (this.vademecum.frenchTeacherName == null) {
          this.vademecum.frenchTeacherName = "";
        }
        if (this.vademecum.friendName == null) {
          this.vademecum.friendName = "";
        }


        this.medicalForm = this.formBuilder.group({
          streetName: [this.vademecum.streetName, Validators.required],
          streetNumber: [this.vademecum.streetNumber, Validators.required],
          city: [this.vademecum.city, Validators.required],
          postCode: [this.vademecum.postCode, Validators.required],
          birthDate: [new Date(this.vademecum.birthDate.toString()), Validators.required],
          startDate: [new Date(this.vademecum.booking!.startDate.toString()), Validators.required],
          endDate: [new Date(this.vademecum.booking!.endDate.toString()), Validators.required],
          targetLanguage: [this.vademecum.targetLanguage, Validators.required],
          doctorName: [this.vademecum.doctorName, Validators.required],
          doctorPhoneNumber: [this.vademecum.doctorPhoneNumber, Validators.required],
          experiencedDiseases: [this.vademecum.experiencedDiseases],
          hasDiabetes: [this.vademecum.hasDiabetes, Validators.required],
          hasAsthma: [this.vademecum.hasAsthma, Validators.required],
          hasHeartTrouble: [this.vademecum.hasHeartTrouble, Validators.required],
          hasEpilepsy: [this.vademecum.hasEpilepsy, Validators.required],
          hasRheumatism: [this.vademecum.hasRheumatism, Validators.required],
          hasPhysicalDisability: [this.vademecum.hasPhysicalDisability, Validators.required],
          hasSkinAffection: [this.vademecum.hasSkinAffection, Validators.required],
          hasSleepDisorder: [this.vademecum.hasSleepDisorder, Validators.required],
          otherDiseases: [this.vademecum.otherDiseases],
          lastSkinTestDate: [lastSkinTestDate],
          skinTestPositive: [this.vademecum.skinTestPositive],
          hadTetanusVaccination: [this.vademecum.hadTetanusVaccination, Validators.required],
          tetanusBoosterYear: [this.vademecum.tetanusBoosterYear],
          hasAllergiesToMedicines: [this.vademecum.hasAllergiesToMedicines, Validators.required],
          allergiesCauseMedicine: [this.vademecum.allergiesCauseMedicine],
          hasAllergiesToSubstances: [this.vademecum.hasAllergiesToSubstances, Validators.required],
          allergiesCauseSubstance: [this.vademecum.allergiesCauseSubstance],
          isIncontinent: [this.vademecum.isIncontinent, Validators.required],
          isQuicklyTired: [this.vademecum.isQuicklyTired, Validators.required],
          isSensitiveToChilling: [this.vademecum.isSensitiveToChilling, Validators.required],
          canSportWithoutRisk: [this.vademecum.canSportWithoutRisk, Validators.required],
          needMedicalTreatment: [this.vademecum.needMedicalTreatment, Validators.required],
          medicinesQuantity: [this.vademecum.medicinesQuantity],
          bloodType: [this.vademecum.bloodType, Validators.required],
          otherInformations: [this.vademecum.otherInformations],
          emergencyContactAddress: [this.vademecum.emergencyContactAddress, Validators.required],

          hasMentalDisorder: [this.vademecum.hasMentalDisorder, Validators.required],
          mentalDisorderDescription: [this.vademecum.mentalDisorderDescription],
          riskDescriptionToSport: [this.vademecum.riskDescriptionToSport]
        },
          {
            validator: [mentalDisorderValidator, medicineAllergyValidator, substanceAllergyValidator, riskSportValidator]
          }
        );

        let languageTestDate = null;
        if (this.vademecum.languageTestDate != null) {
          languageTestDate = moment(this.vademecum.languageTestDate).format("DD/MM/YYYY");
        }

        this.schoolForm = this.formBuilder.group({
          studyDutchInSchool: [this.vademecum.studyDutchInSchool, Validators.required],
          studyEnglishInSchool: [this.vademecum.studyEnglishInSchool, Validators.required],
          studyFrenchInSchool: [this.vademecum.studyFrenchInSchool, Validators.required],
          dutchStudyYears: [this.vademecum.dutchStudyYears],
          englishStudyYears: [this.vademecum.englishStudyYears],
          frenchStudyYears: [this.vademecum.frenchStudyYears],
          dutchStudyHours: [this.vademecum.dutchStudyHours],
          englishStudyHours: [this.vademecum.englishStudyHours],
          frenchStudyHours: [this.vademecum.frenchStudyHours],
          frequencySpeakingWithFamily: [this.vademecum.frequencySpeakingWithFamily, Validators.required],
          frequencySpeakingWithFriends: [this.vademecum.frequencySpeakingWithFriends, Validators.required],
          hasLearningDifficulty: [this.vademecum.hasLearningDifficulty, Validators.required],
          hadLanguageTest: [this.vademecum.hadLanguageTest, Validators.required],
          languageTestDate: [languageTestDate],
          matterToReview: [this.vademecum.matterToReview],
          studyBookName: [this.vademecum.studyBookName],
          dutchTeacherName: [this.vademecum.dutchTeacherName],
          englishTeacherName: [this.vademecum.englishTeacherName],
          frenchTeacherName: [this.vademecum.frenchTeacherName],
          isComingWithFriends: [this.vademecum.isComingWithFriends, Validators.required],
          friendName: [this.vademecum.friendName]
        },
          {
            validator: [dutchSelectValidator, englishSelectValidator, frenchSelectValidator, examDateValidator, friendNameValidator]
          }
        );

        this.personalForm = this.formBuilder.group({
          studentFirstName: [this.vademecum.booking!.student.firstName, Validators.required],
          studentLastName: [this.vademecum.booking!.student.lastName],
          studentMobile: [this.vademecum.studentMobile],
          firstParentMobile: [this.vademecum.firstParentMobile, Validators.required],
          secondParentMobile: [this.vademecum.secondParentMobile],
          firstParentFirstName: [this.vademecum.firstParentFirstName, Validators.required],
          firstParentLastName: [this.vademecum.firstParentLastName, Validators.required],
          secondParentFirstName: [this.vademecum.secondParentFirstName],
          secondParentLastName: [this.vademecum.secondParentLastName]
        });

      });


  }

  makeYears() {
    let thisYear = moment().utc(true).year();
    let newYear = thisYear;
    this.years.push(thisYear);
    for (let i = 0; i < 20; i++) {
      newYear -= 1;
      this.years.push(newYear);
    }
  }

  compareBloodTypeId(b1: BloodType, b2: BloodType) {
    return b1 && b2 ? b1.id === b2.id : b1 === b2;
  }

  compareFrequency(f1: Frequency, f2: Frequency){
    return f1 && f2 ? f1.id === f2.id : f1 === f2;
  }

  sendVademecum() {
    this.vademecum.booking!.startDate = moment(this.medicalForm.value.startDate).utc(true);
    this.vademecum.booking!.endDate = moment(this.medicalForm.value.endDate).utc(true);
    this.vademecum.booking!.student.firstName = this.personalForm.value.studentFirstName;
    this.vademecum.booking!.student.lastName = this.personalForm.value.studentLastName;
    this.httpService.updateVademecum(
      new Vademecum(
        this.medicalForm.value.streetName.trim(),
        this.medicalForm.value.streetNumber.trim(),
        this.medicalForm.value.city.trim(),
        this.medicalForm.value.postCode.trim(),
        moment(this.medicalForm.value.birthDate).utc(true),
        this.medicalForm.value.targetLanguage,
        this.medicalForm.value.doctorName,
        this.medicalForm.value.doctorPhoneNumber,
        this.medicalForm.value.experiencedDiseases,
        this.medicalForm.value.hasDiabetes,
        this.medicalForm.value.hasAsthma,
        this.medicalForm.value.hasHeartTrouble,
        this.medicalForm.value.hasEpilepsy,
        this.medicalForm.value.hasRheumatism,
        this.medicalForm.value.hasPhysicalDisability,
        this.medicalForm.value.hasSkinAffection,
        this.medicalForm.value.hasSleepDisorder,
        this.medicalForm.value.otherDiseases.trim(), 
        moment(this.medicalForm.value.lastSkinTestDate).utc(true),
        this.medicalForm.value.skinTestPositive,
        this.medicalForm.value.hadTetanusVaccination,
        this.medicalForm.value.tetanusBoosterYear,
        this.medicalForm.value.hasAllergiesToMedicines,
        this.medicalForm.value.allergiesCauseMedicine.trim(),
        this.medicalForm.value.hasAllergiesToSubstances,
        this.medicalForm.value.allergiesCauseSubstance.trim(),
        this.medicalForm.value.isIncontinent,
        this.medicalForm.value.isQuicklyTired,
        this.medicalForm.value.isSensitiveToChilling,
        this.medicalForm.value.canSportWithoutRisk,
        this.medicalForm.value.needMedicalTreatment,
        this.medicalForm.value.medicinesQuantity.trim(),
        this.medicalForm.value.bloodType,
        this.medicalForm.value.otherInformations.trim(),
        this.medicalForm.value.emergencyContactAddress.trim(),

        this.medicalForm.value.hasMentalDisorder,
        this.medicalForm.value.mentalDisorderDescription.trim(),
        this.medicalForm.value.riskDescriptionToSport.trim(),
        this.schoolForm.value.studyDutchInSchool,
        this.schoolForm.value.studyEnglishInSchool,
        this.schoolForm.value.studyFrenchInSchool,
        this.schoolForm.value.dutchStudyYears,
        this.schoolForm.value.englishStudyYears,
        this.schoolForm.value.frenchStudyYears,
        this.schoolForm.value.dutchStudyHours,
        this.schoolForm.value.englishStudyHours,
        this.schoolForm.value.frenchStudyHours,
        this.schoolForm.value.frequencySpeakingWithFamily,
        this.schoolForm.value.frequencySpeakingWithFriends,
        this.schoolForm.value.hasLearningDifficulty,
        this.schoolForm.value.hadLanguageTest,
        moment(this.schoolForm.value.languageTestDate).utc(true),
        this.schoolForm.value.matterToReview.trim(),
        this.schoolForm.value.studyBookName.trim(),
        this.schoolForm.value.dutchTeacherName.trim(),
        this.schoolForm.value.englishTeacherName.trim(),
        this.schoolForm.value.frenchTeacherName.trim(),
        this.schoolForm.value.isComingWithFriends,
        this.schoolForm.value.friendName.trim(),
        this.vademecum.booking,
        this.personalForm.value.studentMobile,
        this.personalForm.value.firstParentMobile,
        this.personalForm.value.secondParentMobile,
        this.personalForm.value.firstParentFirstName,
        this.personalForm.value.firstParentLastName,
        this.personalForm.value.secondParentFirstName,
        this.personalForm.value.secondParentLastName,
        this.vademecum.id
      )
    ).subscribe(() => {
      console.log("success");
      this.addSuccess("Success !", "vademecum updated.");
    }, error => {
      this.addError("Error", "Something is wrong.");
      console.error(error);
    });
  }

  addSuccess(summary: string, detail: string) {
    this.messageService.add({ severity: 'success', summary: summary, detail: detail });
  }

  addError(summary: string, detail: string) {
    this.messageService.add({ severity: 'error', summary: summary, detail: detail });
  }

}
